import * as d3 from 'd3'
import { ChartData } from '../types'

export function renderPieChart(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  config: ChartData,
  width: number,
  height: number
) {
  const radius = Math.min(width, height) / 2 - 40
  const g = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`)

  const pie = d3
    .pie<{ label?: string; value: number; color?: string }>()
    .value((d) => d.value)
    .sort(null)

  const arc = d3
    .arc<d3.PieArcDatum<{ label?: string; value: number; color?: string }>>()
    .innerRadius(0)
    .outerRadius(radius)

  const arcs = g.selectAll('arc').data(pie(config.data)).enter().append('g')

  arcs
    .append('path')
    .attr('d', arc)
    .attr('fill', (d) => d.data.color || '#8884d8')
    .attr('stroke', '#1e1a1d')
    .attr('stroke-width', 2)

  const labelArc = d3
    .arc<d3.PieArcDatum<{ label?: string; value: number; color?: string }>>()
    .innerRadius(radius * 0.6)
    .outerRadius(radius * 0.6)

  arcs
    .append('text')
    .attr('transform', (d) => `translate(${labelArc.centroid(d)})`)
    .attr('text-anchor', 'middle')
    .attr('fill', '#fff')
    .attr('font-size', '11px')
    .text((d) => d.data.label || '')
}

export function renderBarChart(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  config: ChartData,
  width: number,
  height: number,
  margin: { top: number; right: number; bottom: number; left: number }
) {
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

  const x = d3
    .scaleBand()
    .domain(config.data.map((d) => d.label || ''))
    .range([0, innerWidth])
    .padding(0.3)

  const y = d3
    .scaleLinear()
    .domain([0, d3.max(config.data, (d) => d.value) || 100])
    .nice()
    .range([innerHeight, 0])

  g.selectAll('rect')
    .data(config.data)
    .enter()
    .append('rect')
    .attr('x', (d) => x(d.label || '') || 0)
    .attr('y', (d) => y(d.value))
    .attr('width', x.bandwidth())
    .attr('height', (d) => innerHeight - y(d.value))
    .attr('fill', (d) => d.color || '#8884d8')
    .attr('rx', 4)

  g.append('g')
    .attr('transform', `translate(0,${innerHeight})`)
    .call(d3.axisBottom(x))
    .selectAll('text')
    .attr('fill', '#cdc4c8')
    .attr('font-size', '10px')

  g.selectAll('.domain, .tick line').attr('stroke', '#4d444b')

  g.append('g').call(d3.axisLeft(y).ticks(5)).selectAll('text').attr('fill', '#cdc4c8').attr('font-size', '10px')
}

export function renderLineChart(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  config: ChartData,
  width: number,
  height: number,
  margin: { top: number; right: number; bottom: number; left: number }
) {
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

  const x = d3
    .scalePoint()
    .domain(config.data.map((d) => d.month || d.label || ''))
    .range([0, innerWidth])

  const y = d3
    .scaleLinear()
    .domain([0, d3.max(config.data, (d) => d.score ?? d.value) || 100])
    .nice()
    .range([innerHeight, 0])

  const line = d3
    .line<{ month?: string; score?: number; label?: string; value: number }>()
    .x((d) => x(d.month || d.label || '') || 0)
    .y((d) => y(d.score ?? d.value))
    .curve(d3.curveMonotoneX)

  g.append('path')
    .datum(config.data)
    .attr('fill', 'none')
    .attr('stroke', '#ffa9fc')
    .attr('stroke-width', 2)
    .attr('d', line)

  g.selectAll('circle')
    .data(config.data)
    .enter()
    .append('circle')
    .attr('cx', (d) => x(d.month || d.label || '') || 0)
    .attr('cy', (d) => y(d.score ?? d.value))
    .attr('r', 5)
    .attr('fill', '#ffa9fc')

  g.append('g')
    .attr('transform', `translate(0,${innerHeight})`)
    .call(d3.axisBottom(x))
    .selectAll('text')
    .attr('fill', '#cdc4c8')
    .attr('font-size', '10px')

  g.selectAll('.domain, .tick line').attr('stroke', '#4d444b')

  g.append('g').call(d3.axisLeft(y).ticks(5)).selectAll('text').attr('fill', '#cdc4c8').attr('font-size', '10px')
}
